import React from "react";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import {
    DA_Title,
    DA_TitleTag,
} from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import { Buttons } from "src/components/Buttons/Buttons.component";
import { useBackgroundColor } from "src/blocks/hooks/useBackgroundColor/useBackgroundColor.hook";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { usePadding } from "src/blocks/hooks/usePadding/usePadding.hook";
import { LottieAnimation } from "src/components/LottieAnimation/LottieAnimation.component";
import { CMS_SITE_ORIGIN } from "@danishagro/shared/src/constants/urlRoot.constants";
import { TextGraphicsProps } from "./TextGraphics.interface";
import { EuropeMap } from "./components/EuropeMap/EuropeMap.component";
import { usePosition } from "./hooks/usePosition.hook";
import S from "./TextGraphics.module.scss";

interface LottieProps {
    lottieURL?: string;
    umbracoFile?: string;
    lottieId?: string;
}

const TextGraphics = (props: TextGraphicsProps) => {
    // Settings
    const { backgroundColorClass, alternativeButtonTheme, hasBackgroundColor } = useBackgroundColor(
        props.backgroundColor
    );
    const { positionClassName } = usePosition(props.position, S);
    const { paddingClass } = usePadding(props.isCompact, hasBackgroundColor);

    const getCmsCdnUrl = globalThis?.cmsCdnUrl;

    // Graphics content
    const getInnerHtml = () => {
        const graphicsContent = props?.graphics && props?.graphics[0];

        switch (graphicsContent?.alias) {
            case "europe":
                return <EuropeMap regions={props.regions} />;
            case "lottie": {
                const lottieProps = graphicsContent?.props as LottieProps;

                let lottieUrl = "";

                if (lottieProps?.lottieURL) {
                    lottieUrl = lottieProps?.lottieURL;
                } else if (lottieProps?.umbracoFile) {
                    lottieUrl = getCmsCdnUrl
                        ? `${lottieProps?.umbracoFile}`
                        : `${CMS_SITE_ORIGIN}/getmediafile?url=${lottieProps?.umbracoFile}`;
                }

                return lottieUrl ? <LottieAnimation key={lottieUrl} url={lottieUrl} /> : null;
            }
            default:
                return null;
        }
    };

    return (
        <div className={cn(S.wrapper, paddingClass, backgroundColorClass, positionClassName)}>
            <DA_Container noVerticalPadding>
                <div className={S.inner}>
                    {/** Content */}
                    <div className={S.textWrapper}>
                        {/** Tagline */}
                        {props.tagline ? <span className={S.tagline}>{props.tagline}</span> : null}
                        {/** Title */}
                        {props.headline ? (
                            <DA_Title h2 tag={DA_TitleTag.H2} noMargin fullWidth>
                                {props.headline}
                            </DA_Title>
                        ) : null}

                        {/** Text */}
                        {props.text ? (
                            <div className={S.text}>
                                <DA_Text html={props.text} noPadding />
                            </div>
                        ) : null}

                        {/** Buttons */}
                        {props.buttons && props.buttons.length > 0 && (
                            <div className={S.button}>
                                <Buttons buttons={props.buttons} theme={alternativeButtonTheme} />
                            </div>
                        )}
                    </div>

                    {/* Graphics content */}
                    <div className={S.graphicsWrapper}>{getInnerHtml()}</div>
                </div>
            </DA_Container>
        </div>
    );
};

export default TextGraphics;
